import MediaThemeSutro from "@player.style/sutro/react";
import { PortableTextComponents } from "@portabletext/react";
import {
  getFileAsset,
  SanityFileSource,
  getImageDimensions,
} from "@sanity/asset-utils";
import imageUrlBuilder from "@sanity/image-url";
import { kebabCase } from "change-case";
import ReactPlayer from "react-player";
import { PortableTextBlock } from "sanity";

import { mdxComponents } from "@/components/mdxComponents";
import { Anchor } from "@/components/ui/typography";
import { cn } from "@/lib/utils";
import { sanityClient } from "@/sanity-client";

const builder = imageUrlBuilder(sanityClient);

export function toPlainText(blocks: PortableTextBlock[] = []) {
  return (
    blocks
      // loop through each block
      .map((block) => {
        // if it's not a text block with children,
        // return nothing
        if (block._type !== "block" || !block.children) {
          return "";
        }
        // loop through the children spans, and join the
        // text strings
        return (block.children as Array<{ text: string }>)
          .map((child) => child.text)
          .join("");
      })
      // join the paragraphs leaving split by two linebreaks
      .join("\n\n")
  );
}

export function getHeadingId(value: PortableTextBlock) {
  return kebabCase(toPlainText([value]));
}

export const components: PortableTextComponents = {
  marks: {
    link: ({ value, children }) => {
      return (
        <Anchor href={(value as { href: string }).href}>{children}</Anchor>
      );
    },
    code: ({ children }) => (
      <code className="font-mono [&:where(code)]:text-sm bg-slate-4 dark:bg-slatedark-4 px-2 py-0.5 rounded-md">
        {children}
      </code>
    ),
  },
  block: {
    h1: ({ value, children }) => (
      <mdxComponents.h1 id={getHeadingId(value as PortableTextBlock)}>
        {children}
      </mdxComponents.h1>
    ),
    h2: ({ value, children }) => (
      <mdxComponents.h2 id={getHeadingId(value as PortableTextBlock)}>
        {children}
      </mdxComponents.h2>
    ),
    h3: ({ value, children }) => (
      <mdxComponents.h3 id={getHeadingId(value as PortableTextBlock)}>
        {children}
      </mdxComponents.h3>
    ),
    h4: ({ value, children }) => (
      <mdxComponents.h4 id={getHeadingId(value as PortableTextBlock)}>
        {children}
      </mdxComponents.h4>
    ),
    normal: ({ children }) => <mdxComponents.p>{children}</mdxComponents.p>,
    blockquote: ({ children }) => (
      <blockquote
        className={cn(
          "shadow-lg border border-yellow-5 dark:border-yellowdark-5 rounded-lg p-4 my-8",
          "bg-yellow-subtle text-yellow-normal",
        )}
      >
        {children}
      </blockquote>
    ),
  },
  types: {
    image: ({ value }) => {
      const url = builder
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
        .image(value as any)
        .auto("format")
        .width(1000)
        .url();
      const dimensions = getImageDimensions({ url });
      return (
        <div className="border border-slate-6 dark:border-slatedark-6 rounded-lg overflow-hidden my-8">
          <img
            src={url}
            alt=""
            height={dimensions.height}
            width={dimensions.width}
          />
        </div>
      );
    },
    file: ({ value }) => {
      const file = getFileAsset(value as SanityFileSource, {
        projectId: "pntpjov7",
        dataset: "production",
      });

      return (
        <MediaThemeSutro className="my-8 border border-slate-6 dark:border-slatedark-6 rounded-lg overflow-hidden aspect-video block bg-black">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            slot="media"
            src={file.url}
            playsInline
            crossOrigin=""
            className="w-full h-full object-fit"
            tabIndex={-1}
          />
        </MediaThemeSutro>
      );
    },
    code: ({ value }) => (
      <div
        className="my-8"
        // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
        dangerouslySetInnerHTML={{ __html: (value as { code: string }).code }}
      />
    ),
    youtube: ({ value }) => {
      const { url } = value as { url: string };
      return <ReactPlayer src={url} />;
    },
  },
  list: {
    bullet: ({ children }) => <mdxComponents.ul>{children}</mdxComponents.ul>,
    number: ({ children }) => <mdxComponents.ol>{children}</mdxComponents.ol>,
  },
  listItem: {
    bullet: ({ children }) => <mdxComponents.li>{children}</mdxComponents.li>,
    number: ({ children }) => <mdxComponents.li>{children}</mdxComponents.li>,
  },
  hardBreak: () => <mdxComponents.hr />,
};

export const componentsNew: PortableTextComponents = {
  marks: {
    link: ({ value, children }) => {
      return (
        <Anchor href={(value as { href: string }).href}>{children}</Anchor>
      );
    },
    code: ({ children }) => (
      <code className="font-mono [&:where(code)]:text-sm bg-slate-4 dark:bg-slatedark-4 px-2 py-0.5 rounded-md">
        {children}
      </code>
    ),
  },
  block: {
    h1: ({ value, children }) => (
      <h1
        className="scroll-m-20 text-5xl md:text-6xl font-extrabold tracking-tight instrument-serif"
        id={getHeadingId(value as PortableTextBlock)}
      >
        {children}
      </h1>
    ),
    h2: ({ value, children }) => (
      <h2
        className="mt-16 mb-4 scroll-m-20 border-b border-slate-7 dark:border-slatedark-7 pb-3 text-3xl md:text-5xl font-semibold tracking-tight transition-colors first:mt-0 instrument-serif"
        id={getHeadingId(value as PortableTextBlock)}
      >
        {children}
      </h2>
    ),
    h3: ({ value, children }) => (
      <mdxComponents.h3 id={getHeadingId(value as PortableTextBlock)}>
        {children}
      </mdxComponents.h3>
    ),
    h4: ({ value, children }) => (
      <mdxComponents.h4 id={getHeadingId(value as PortableTextBlock)}>
        {children}
      </mdxComponents.h4>
    ),
    normal: ({ children }) => (
      <p className="my-4 leading-relaxed text-lg">{children}</p>
    ),
    blockquote: ({ children }) => (
      <blockquote
        className={cn(
          "shadow-lg border border-yellow-5 dark:border-yellowdark-5 rounded-lg p-4 my-8",
          "bg-yellow-subtle text-yellow-normal",
        )}
      >
        {children}
      </blockquote>
    ),
  },
  types: {
    image: ({ value }) => {
      const url = builder
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
        .image(value as any)
        .auto("format")
        .width(1000)
        .url();
      const dimensions = getImageDimensions({ url });

      return (
        <div className="w-fit mx-auto flex border-2 border-slate-6 dark:border-slatedark-6 rounded-lg overflow-hidden my-12">
          <img
            src={url}
            alt=""
            className="max-h-[70vh]"
            height={dimensions.height}
          />
        </div>
      );
    },
    file: ({ value }) => {
      const file = getFileAsset(value as SanityFileSource, {
        projectId: "pntpjov7",
        dataset: "production",
      });

      return (
        <MediaThemeSutro className="my-8 border border-slate-6 dark:border-slatedark-6 rounded-lg overflow-hidden aspect-video block bg-black">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            slot="media"
            src={file.url}
            playsInline
            crossOrigin=""
            className="w-full h-full object-fit"
            tabIndex={-1}
          />
        </MediaThemeSutro>
      );
    },
    code: ({ value }) => (
      <div
        className="my-8"
        // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
        dangerouslySetInnerHTML={{ __html: (value as { code: string }).code }}
      />
    ),
    youtube: ({ value }) => {
      const { url } = value as { url: string };
      return <ReactPlayer src={url} />;
    },
  },
  list: {
    bullet: ({ children }) => <mdxComponents.ul>{children}</mdxComponents.ul>,
    number: ({ children }) => <mdxComponents.ol>{children}</mdxComponents.ol>,
  },
  listItem: {
    bullet: ({ children }) => <mdxComponents.li>{children}</mdxComponents.li>,
    number: ({ children }) => <mdxComponents.li>{children}</mdxComponents.li>,
  },
  hardBreak: () => <mdxComponents.hr />,
};
